/**
 * Neutral padding (W1-04 core-ir; DESIGN.md §6.10, rpe-format.md F§6.2, F§7.2, F§9.1, F§9.4, F§12, F§15).
 *
 * Applied only to lines created in the editor (`Line.origin === 'editor'`), only in the RPE-compatible export
 * profile, and only in forms Phira evaluates exactly like "absent", so preview and export stay identical:
 *  - eventLayers padded to 4 slots with `null` (imported lines keep their slot count and null / {} style);
 *  - layer 0 gets one constant 0 event [0, 1/2 beat] for each of alpha, moveX, moveY, rotate it lacks: absent alpha
 *    is the constant 0, and a 0 layer adds nothing to the right-associative f32 layer sum (`0 + x = x`);
 *  - speed gets a constant 0 only when the line has no speed track on ANY layer (H = 0 either way); never next to
 *    other speed layers, because extra breakpoints shift the legacy integrator's `b - 1e-4` sampling;
 *  - scale lists are never padded (absent scale is not the same as 1.0, F§9.1);
 *  - `extended.inclineEvents`: one constant 0 event when absent (incline is frozen at its t = 0 value, 0 either way);
 *  - absent note controls get RPE's inert 2-point default; absent skewControl gets RPE's default.
 * Group, bpmfactor, anchor, isGif and numOfNotes are always written by line lowering.
 *
 * The padding event lasts HALF a beat (CR-W3-02a-8): Lower's lone-key event, like RPE's own default event, lasts one
 * beat, and the editor makes lone keys holding 0 at beat 0 all the time (a new line's rotate, its moveX at x = 0, a
 * helper line's alpha). With a different length a chart read back without phiedit.json still tells padding from a
 * key, so `unpadEditorLine` removes exactly the padding again (UX-026). Phira evaluates either length as the
 * constant 0; the length only enters max_time (F§1) for a chart with nothing after half a beat.
 */

import { Defaults, RPE_DEFAULT_CONTROL_END_X } from '../model/defaults';
import { requiresLeadInKey } from '../model/props';
import type { LineProp } from '../model/types';
import { Beats } from '../time/beat';
import type { Triple } from '../time/types';
import { jsonEquals, setOwn } from '../util/json';
import type { Json } from '../util/types';
import { isZeroPoints, LINEAR_FIELDS } from './eventFields';
import { controlOut, EXT_PROPS_IN_ORDER, LAYER_PROPS_IN_ORDER, listKeyOf, type MutableIrLine } from './irShape';
import { tripleEquals, tripleIn } from './triples';
import type { IrEvent, IrEventList, IrExtended, IrLayer, IrLine } from './types';

/** Slot count RPE writes (layers 0-3; its "layer 4" tab is `extended`). */
export const RPE_LAYER_SLOTS = 4;

/** Start of the padding event: beat 0. */
const PADDING_START: Triple = [0, 0, 1];
/** End of the padding event: half a beat, unlike any lone-key or RPE default event (one beat). */
export const PADDING_END: Triple = [0, 1, 2];

/** The constant padding event [0, 1/2 beat] with value 0. */
export function paddingEvent(): IrEventList<number> {
  const e: IrEvent<number> = {
    startTime: PADDING_START,
    endTime: PADDING_END,
    start: 0,
    end: 0,
    ...LINEAR_FIELDS,
    linkgroup: 0,
    extra: [],
    origin: 'padding',
    srcKey: null,
  };
  return [e];
}

/** RPE's default skewControl (Phira ignores skew). */
export function rpeDefaultSkewControl(): Json {
  return [
    { x: 0, easing: 1, skew: 0 },
    { x: RPE_DEFAULT_CONTROL_END_X, easing: 1, skew: 0 },
  ];
}

type MutableLayer = { -readonly [K in keyof IrLayer]: IrLayer[K] };
type MutableExtended = { -readonly [K in keyof IrExtended]: IrExtended[K] };

const PADDED_LAYER_KEYS = ['alphaEvents', 'moveXEvents', 'moveYEvents', 'rotateEvents'] as const;

/**
 * Pads an editor line in place (RPE-compatible profile). `hasSpeedTrack`: the line has a speed track on some layer.
 * Returns true when anything was added.
 */
export function padEditorLine(ir: MutableIrLine, hasSpeedTrack: boolean): boolean {
  let changed = false;
  const layers = ir.eventLayers;
  while (layers.length < RPE_LAYER_SLOTS) {
    layers.push(null);
    changed = true;
  }
  const slot0 = layers[0] ?? null;
  const layer0: MutableLayer = slot0 === null ? { nullKeys: [], extra: [] } : { ...slot0 };
  let layerChanged = slot0 === null;
  for (const key of PADDED_LAYER_KEYS) {
    if (layer0[key] !== undefined) continue;
    layer0[key] = paddingEvent();
    layerChanged = true;
  }
  if (!hasSpeedTrack && layer0.speedEvents === undefined) {
    layer0.speedEvents = paddingEvent();
    layerChanged = true;
  }
  if (layerChanged) {
    layer0.nullKeys = layer0.nullKeys.filter((k) => layer0[k as keyof MutableLayer] === undefined);
    layers[0] = layer0;
    changed = true;
  }
  const ext = ir.extended;
  if (ext === 'absent' || ext === null || ext.inclineEvents === undefined) {
    const base: MutableExtended =
      ext === 'absent' || ext === null ? { ignoredLists: [], nullKeys: [], extra: [] } : { ...ext };
    base.inclineEvents = paddingEvent();
    base.nullKeys = base.nullKeys.filter((k) => k !== 'inclineEvents');
    ir.extended = base;
    changed = true;
  }
  for (const key of ['pos', 'alpha', 'size', 'y'] as const) {
    const field = `${key}Control` as const;
    if (ir[field] !== undefined) continue;
    ir[field] = controlOut(Defaults.rpeDefaultControl(key));
    changed = true;
  }
  if (ir.skewControl === undefined) {
    ir.skewControl = rpeDefaultSkewControl();
    changed = true;
  }
  return changed;
}

// ---------------------------------------------------------------------------------------------------------------
// Recognition on import (UX-026)
// ---------------------------------------------------------------------------------------------------------------

/**
 * True for an event Lower wrote as padding: by its origin while in memory, by its exact shape when read from a file
 * (constant 0 over [0, 1/2 beat] in normalised triples, linear fields, link group 0, no unknown keys).
 */
export function isPaddingEvent(e: IrEvent<unknown>): boolean {
  if (e.origin === 'padding') return true;
  return (
    e.origin === 'segment' &&
    e.srcKey === null &&
    tripleEquals(e.startTime, PADDING_START) &&
    tripleEquals(e.endTime, PADDING_END) &&
    Object.is(e.start, 0) &&
    Object.is(e.end, 0) &&
    e.easingType === LINEAR_FIELDS.easingType &&
    e.easingLeft === LINEAR_FIELDS.easingLeft &&
    e.easingRight === LINEAR_FIELDS.easingRight &&
    e.bezier === LINEAR_FIELDS.bezier &&
    isZeroPoints(e.bezierPoints) &&
    e.linkgroup === 0 &&
    e.extra.length === 0
  );
}

function isPaddingList(list: readonly IrEvent<unknown>[] | undefined): boolean {
  return list?.length === 1 && isPaddingEvent(list[0] as IrEvent<unknown>);
}

/** `o` without the given keys. */
function omit<T extends object>(o: T, keys: ReadonlySet<string>): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (!keys.has(k)) setOwn(out, k, v);
  return out as T;
}

/** Equality of IR values as the JSON writer sees them: event origins and source keys aside, undefined = absent. */
function sameWritten(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => sameWritten(x, b[i]));
  const keys = (o: object): string[] =>
    Object.entries(o)
      .filter(([k, v]) => v !== undefined && k !== 'origin' && k !== 'srcKey')
      .map(([k]) => k)
      .sort();
  const ka = keys(a);
  const kb = keys(b);
  if (ka.length !== kb.length || ka.some((k, i) => k !== kb[i])) return false;
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  return ka.every((k) => sameWritten(ra[k], rb[k]));
}

/** IrLine fields padEditorLine writes. */
const PAD_FIELDS = [
  'eventLayers',
  'extended',
  'posControl',
  'alphaControl',
  'sizeControl',
  'yControl',
  'skewControl',
] as const satisfies readonly (keyof IrLine)[];

/** Every list of an extrapolating property starts exactly at beat 0, as the lists of editor tracks do. */
function leadInListsAtZero(ir: IrLine): boolean {
  const atZero = (prop: LineProp, list: unknown): boolean => {
    if (!requiresLeadInKey(prop) || !Array.isArray(list) || list.length === 0) return true;
    const t = tripleIn((list[0] as IrEvent<unknown>).startTime);
    return t !== null && Beats.eq(t.beat, Beats.ZERO);
  };
  for (const slot of ir.eventLayers) {
    if (slot === null) continue;
    for (const prop of LAYER_PROPS_IN_ORDER) if (!atZero(prop, slot[listKeyOf(prop) as keyof IrLayer])) return false;
  }
  const ext = ir.extended;
  if (ext === null || ext === 'absent') return true;
  return EXT_PROPS_IN_ORDER.every((prop) => atZero(prop, ext[listKeyOf(prop) as keyof IrExtended]));
}

/**
 * The inverse of `padEditorLine` for a line read back from a file (a chart exported without phiedit.json, UX-026):
 * when the line holds at least one padding list (layer 0 alpha / moveX / moveY / rotate, layer 0 speed while no
 * other layer has speed, extended incline) and padding the line without its padding lists, RPE default controls and
 * default skewControl gives back exactly what the file says (so it can only have been written that way), returns
 * the line without them; the lifter then makes it an editor line again, and the next export pads it identically.
 * Otherwise (an imported line, or a line padding cannot have produced) returns null and the line is lifted as is.
 */
export function unpadEditorLine(ir: IrLine): IrLine | null {
  const slots = ir.eventLayers;
  const slot0 = slots[0] ?? null;
  const ext = ir.extended;
  if (slots.length < RPE_LAYER_SLOTS || slot0 === null || ext === null || ext === 'absent') return null;
  const layerDrop = new Set<string>();
  for (const key of PADDED_LAYER_KEYS) if (isPaddingList(slot0[key])) layerDrop.add(key);
  const otherSpeed = slots.some((s, i) => i > 0 && s?.speedEvents !== undefined);
  if (!otherSpeed && isPaddingList(slot0.speedEvents)) layerDrop.add('speedEvents');
  const inclineDrop = isPaddingList(ext.inclineEvents);
  if (layerDrop.size === 0 && !inclineDrop) return null;
  // Editor tracks of extrapolating properties start at beat 0 (and never before it).
  if (!leadInListsAtZero(ir)) return null;

  const lineDrop = new Set<string>();
  for (const key of ['pos', 'alpha', 'size', 'y'] as const) {
    const field = `${key}Control` as const;
    const points = ir[field];
    if (points !== undefined && sameWritten(points, controlOut(Defaults.rpeDefaultControl(key)))) lineDrop.add(field);
  }
  if (ir.skewControl !== undefined && jsonEquals(ir.skewControl, rpeDefaultSkewControl())) lineDrop.add('skewControl');
  const out: IrLine = {
    ...omit(ir, lineDrop),
    eventLayers: [omit(slot0, layerDrop), ...slots.slice(1)],
    extended: inclineDrop ? omit(ext, new Set(['inclineEvents'])) : ext,
  };

  const padded: MutableIrLine = { ...out, eventLayers: [...out.eventLayers] };
  padEditorLine(
    padded,
    padded.eventLayers.some((s) => s?.speedEvents !== undefined),
  );
  return PAD_FIELDS.every((f) => sameWritten(padded[f], ir[f])) ? out : null;
}
